/**
 * ModelIntake — the `/model` intake surface, canvas states A→D.
 *
 * Spec: `thinking/design/handoffs/training_shift/Model Intake.html`. Reuses the portrait Intake's
 * drop-tile interaction (click/drag/drop, `validImageTypes` filter), with a per-slot
 * verifying/ok/failed state, scaled to a MIN-MAX photo grid (constants in `useModelIntake`) instead of a fixed 5-slot face grid.
 *
 * State derivation is local, not a reducer: `useModelIntake` owns slot data, `intakeView.ts` turns it
 * into tiles, counts, the ambient tone and the state line (the 2026-10-08 canvas), and this
 * component renders them and carries the Start refusals.
 */
import {useCallback, useEffect, useRef, useState} from 'react'
import {Form, Link, useFetcher, useLocation, useRouteLoaderData} from 'react-router'
import type {FunnelConfig} from '@core/products/funnel'
import {useConsentGate} from '@core/consent/useConsentGate'
import {SignupGate} from '@core/auth-ui/SignupGate/SignupGate'
import {validImageTypes} from '@product/narcis/utils/imageProcessing'
import {narcisTrack} from '@product/narcis/utils/analytics'
import {NARCIS_PACK_SAMPLES_BASE_URL} from '@product/narcis/config'
import {CONTACT_EMAIL, GUARANTEE, priceLine} from '@product/narcis/proposition'
import {Notice, type NoticeKind} from '@core/ui/Notice'
import type {loader as rootLoader} from '@root'
import {
  MIN_TRAINING_PHOTOS,
  MAX_TRAINING_PHOTOS,
  diffRetrainSet,
  type UseModelIntakeReturn,
  type RetrainSetDiff,
} from '@core/hooks/useModelIntake'
import styles from './ModelIntake.module.scss'
import {
  PAYMENT_RETRIES,
  PAYMENT_RETRY_MS,
  buildTiles,
  deriveTone,
  footLine,
  ledgerRows,
  refusalMarks,
  startControl,
  stateLine,
  summarize,
  type PhotoMark,
  type StartActionResponse,
  type Tone,
} from './intakeView'
import {
  RETURN_CHECKOUT_PARAM,
  RETURN_CONSENT_PARAM,
  RETURN_SESSION_PARAM,
  checkoutStartedOnce,
  consentReturnAddress,
  pendingAddress,
  purchaseEventOnce,
  readCheckoutReturn,
  readConsentReturn,
  shouldAutoStart,
  withoutParams,
  type CheckoutOutcome,
} from './checkoutReturn'

/** `localStorage`, or null where reading the accessor throws (private window, blocked site data). */
function safeLocalStorage(): Storage | null {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

// Every reason the start-training action can actually return (`_layout.model.tsx`'s action) —
// model_training_refused must cover every one (restored 2026-08-01, plan
// 2026_08_01_ga4_wiring_defect_remediation.md D4: narcis dropped this under decision 8 in the
// funnel cutover; ovid kept it — see that product's ModelIntake.tsx for the same set). A user
// dead-ended on any of these must be visible in GA4, not silent — the operator's own read is that
// this is the photo-minimum effort-wall signal.
const CONSENT_SENTENCE = 'I agree that my photos are used to create my private model.'
const REFUSAL_REASONS = new Set(['already-training', 'invalid-photo-count', 'consent-required', 'photos-unavailable'])
function isTrackedRefusal(reason: string): reason is 'already-training' | 'invalid-photo-count' | 'consent-required' | 'photos-unavailable' {
  return REFUSAL_REASONS.has(reason)
}

const TONE_CLASS: Record<Tone, string> = {
  collecting: styles.toneCollecting,
  working: styles.toneWorking,
  worth: styles.toneWorth,
  needs: styles.toneNeeds,
  ready: styles.toneReady,
}
const LEDGER_TONE_CLASS = {
  ok: styles.toneReady,
  needs: styles.toneNeeds,
  working: styles.toneWorking,
  worth: styles.toneWorth,
}
const NOTICE_KIND: Record<'cancelled' | 'failed' | 'paid', NoticeKind> = {
  cancelled: 'info',
  failed: 'error',
  paid: 'success',
}

// Guide-tile imagery: generated in the cannes-riviera register (faceless Z-Image, round config
// `generation/scripts/rounds/round_narcis_model_guide_zimage.json`), staged under the pack-samples
// S3 prefix so it rides the existing CloudFront S3-origin behavior.
const GUIDE_TILES_BASE = `${NARCIS_PACK_SAMPLES_BASE_URL}/_model-guide`
const GUIDANCE_DO = [
  {caption: 'daylight selfie', note: 'good light', img: `${GUIDE_TILES_BASE}/do1-daylight.webp`},
  {caption: '3⁄4 angle, shoulders up', note: 'different angles', img: `${GUIDE_TILES_BASE}/do2-angle.webp`},
  {caption: 'different day & place', note: 'different days and places', img: `${GUIDE_TILES_BASE}/do3-variety.webp`},
]
const GUIDANCE_DONT = [
  {caption: 'sunglasses, hat', note: 'covered face', img: `${GUIDE_TILES_BASE}/dont1-covered.webp`},
  {caption: 'group shot', note: 'other people', img: `${GUIDE_TILES_BASE}/dont2-group.webp`},
  {caption: 'same pose ×5', note: 'almost the same photo', img: `${GUIDE_TILES_BASE}/dont3-duplicates.webp`},
]

interface ModelIntakeProps {
  intake: UseModelIntakeReturn
  isAuthenticated: boolean
  funnel: FunnelConfig
  authAction: string
  /** DB-state head note (`_layout.model.tsx`'s loader) — 'ready' when Start here means retrain,
   *  'failed' when this intake is the recovery path after a training run that didn't complete.
   *  Undefined (no note) when the user has no model row at all — unchanged intake. */
  modelNote?: 'ready' | 'failed'
  /** Fires once Start-training actually queues (narcis wall plan step 4 — the wall's own mount of
   *  this component, `products/narcis/wall/IdentityIntake.tsx`). `/model`'s own route does not pass
   *  this — that page has nowhere to "close" onto, the loader's next read is what morphs it to the
   *  wait view. Undefined is a safe no-op, not a required prop, so `/model` stays byte-for-byte
   *  unaffected by this addition. */
  onDone?: () => void
  /** The address that reopens this intake on its page — where the internal `SignupGate` sends an
   *  anonymous visitor once signed in, and where Stripe Checkout returns the buyer (narcis wall
   *  plan step 6 § 2, "`/model` parked ... nothing else links to /model"). Defaults to `/model`;
   *  the wall and the pack page pass their own `?intake=1` address. */
  signupRedirectTo?: string
}

export function ModelIntake({intake, isAuthenticated, funnel, authAction, modelNote, onDone, signupRedirectTo = '/model'}: ModelIntakeProps) {
  const {
    slots,
    isFull,
    addFiles,
    removeSlot,
    skippedNotices,
    clearSkippedNotices,
    setCheckFlags,
    setAdvisories,
    prefillKeys,
    clearStash,
  } = intake
  // The server answered `signup-required`: the sign-in opens in the rail, over the photos.
  const [sessionLost, setSessionLost] = useState(false)
  // Pay at launch: what Start asks (root loader) — the price, through Stripe Checkout, or nothing
  // again for a payment not yet consumed.
  const rootData = useRouteLoaderData<typeof rootLoader>('root')
  const modelPayment = rootData?.modelPayment ?? 'pay'
  // The currency this viewer's Checkout charges, as the page writes it and GA4 reports it.
  const currency = rootData?.modelCurrency ?? 'usd'
  // The checkout is a document form: from its submit until Stripe's page replaces this one, the
  // button holds. A fetcher here left the page's own requests in flight as it unloaded, and Safari
  // reported them as a route error until Stripe answered (dev, 2026-10-07).
  const checkoutSubmittedRef = useRef(false)
  const [checkoutSubmitting, setCheckoutSubmitting] = useState(false)
  useEffect(() => {
    // Back from Stripe through the back-forward cache: the page is as it was left, mid-submit.
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return
      checkoutSubmittedRef.current = false
      setCheckoutSubmitting(false)
    }
    window.addEventListener('pageshow', onPageShow)
    return () => window.removeEventListener('pageshow', onPageShow)
  }, [])
  // The Checkout Session the buyer came back from (`?session_id=`): Start runs on it, and runs by
  // itself once the photos are back on the bench. `checkoutNotice` is the line a cancelled or
  // failed checkout leaves. The address-bar rules are `checkoutReturn.ts`'s.
  const [returnSessionId, setReturnSessionId] = useState<string | null>(null)
  const [checkoutNotice, setCheckoutNotice] = useState<{outcome: CheckoutOutcome; text: string} | null>(null)
  // Back from `/checkout/model` with "a purchase is already open": Start runs on it, no payment asked.
  const [purchaseOpen, setPurchaseOpen] = useState(false)
  const autoStartedRef = useRef(false)
  // The page's own address, without the return — what the address bar becomes once the paid
  // Start is confirmed.
  const settledAddressRef = useRef<string | null>(null)
  const location = useLocation()
  useEffect(() => {
    // Read from the address bar, not the router's location: the page that mounted this intake may
    // have already rewritten it (the pack page drops its own `intake=1`).
    const {pathname, search} = window.location
    const {sessionId, outcome, notice, cancelled} = readCheckoutReturn(search)
    if (sessionId) {
      setReturnSessionId(sessionId)
      setCheckoutNotice(null)
      settledAddressRef.current = withoutParams(pathname, search, [RETURN_SESSION_PARAM, RETURN_CHECKOUT_PARAM])
      window.history.replaceState(window.history.state, '', pendingAddress(signupRedirectTo, sessionId))
    } else if (outcome && notice) {
      setCheckoutNotice({outcome, text: notice})
      if (outcome === 'paid') setPurchaseOpen(true)
      if (cancelled) narcisTrack('checkout_cancelled', {})
      window.history.replaceState(window.history.state, '', withoutParams(pathname, search, [RETURN_CHECKOUT_PARAM]))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-read on navigation only
  }, [location.key, location.search])

  const [noteDismissed, setNoteDismissed] = useState(false)
  // Retrain intake is additive (funnel spec §10) — the ONLY way this component ever renders with
  // `modelNote === 'ready'` is the explicit "Add photos & retrain" click from Model Home
  // (`_layout.model.tsx`'s render branching: a ready+settled owner without `showIntake` renders
  // Model Home instead, never falls through here) — so this prop doubles, honestly, as "this IS
  // the retrain flow," no separate flag needed. Frozen for the mount via `useState`'s lazy
  // initializer: `modelNote` cannot change out from under an already-mounted intake (Start moves
  // `userModel.status` to 'training' only on the NEXT navigation-triggering loader read).
  const [isRetrain] = useState(() => modelNote === 'ready')
  // Intake consent (funnel spec §8, amended §10). A first-time Start asks it on the first press and
  // runs on the press after the box is ticked: the pack pages' two-press gate, without its browser
  // memory (this consent is the server's to require on every Start, and is not the ToS). A retrain
  // already granted it at first training: the box shows ticked and locked, and Start does not ask
  // again. The server enforces the rule independently (`_layout.model.tsx`'s start-training action,
  // `shouldRefuseForConsent`) — the client only orders the presses.
  // No dictionary event: `training_started`/`retrain_started` already imply consent (spec §8's own
  // note), so there's nothing new to fire here.
  const gate = useConsentGate({remember: false})
  // Back from the sign-in the Start asked for: the box comes back ticked, once, and the address bar
  // is cleaned (`checkoutReturn.ts`, RETURN_CONSENT_PARAM). Read from the address bar for the same
  // reason as the Checkout return above.
  useEffect(() => {
    const {pathname, search} = window.location
    if (!readConsentReturn(search)) return
    window.history.replaceState(window.history.state, '', withoutParams(pathname, search, [RETURN_CONSENT_PARAM]))
    gate.press()
    gate.tick(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per mount; `gate.press` reads live refs
  }, [])
  const consentBoxRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (gate.open) consentBoxRef.current?.focus()
  }, [gate.open, gate.cardProps.focusToken])
  const startFetcher = useFetcher<StartActionResponse>()
  const inputRef = useRef<HTMLInputElement>(null)
  // GA4 funnel: fires once per distinct Start-training response. submittedPhotoCountRef
  // captures the count AT SUBMIT time (handleStartClick) rather than reading okCount when the
  // response lands — the bench isn't frozen for an authenticated Start, so a photo could be
  // added while the request is in flight; the submitted count must not drift with it.
  // lastTrackedDataRef keys on response identity (not a one-shot flag) — a non-tracked error
  // (e.g. consent-required) must not block a legitimate fire on a subsequent retry.
  const lastTrackedDataRef = useRef<StartActionResponse | null>(null)
  const submittedPhotoCountRef = useRef(0)
  // retrain_started's payload (funnel spec §10) — captured at the SAME submit-time moment as
  // submittedPhotoCountRef above, for the same reason (the bench isn't frozen; the diff must
  // reflect what was actually SUBMITTED, not whatever slots hold when the response lands).
  // Only ever set when isRetrain; training_started's branch below never reads it.
  const retrainDiffRef = useRef<RetrainSetDiff | null>(null)
  // The 'ready' note is a short standing fact (retraining always replaces the served model) —
  // not dismissable. The 'failed' note is a one-time recovery nudge — dismissable.
  const showModelNote = modelNote === 'ready' || (modelNote === 'failed' && !noteDismissed)

  // Photos set aside by a refused Start, by s3Key: they stay on the bench, marked, until the user
  // removes or replaces them or continues without them.
  const [marks, setMarks] = useState<Record<string, PhotoMark>>({})
  const [consentRing, setConsentRing] = useState(false)
  const [paymentGaveUp, setPaymentGaveUp] = useState(false)
  const paymentTriesRef = useRef(0)

  // The rail's consent, price and Start: below 900px it sits after the whole grid, and the state line
  // at the top points to it once the set is ready.
  const startAreaRef = useRef<HTMLDivElement>(null)

  const tiles = buildTiles(slots, setCheckFlags, marks)
  const summary = summarize(tiles, setAdvisories)
  const accepted = summary.accepted
  const isReady = accepted >= MIN_TRAINING_PHOTOS
  const marked = summary.unavailable + summary.foreign
  const handleFiles = useCallback(
    (incoming: File[]) => {
      if (incoming.length > 0) addFiles(incoming)
    },
    [addFiles],
  )

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      handleFiles(Array.from(e.dataTransfer.files))
    },
    [handleFiles],
  )

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'copy'
  }, [])

  // Paste: the clipboard's images join the bench like dropped files. A paste into a text field is
  // that field's own.
  useEffect(() => {
    const onPaste = (event: ClipboardEvent) => {
      const target = event.target as HTMLElement | null
      if (target && (target.isContentEditable || /^(INPUT|TEXTAREA)$/.test(target.tagName))) return
      const images = Array.from(event.clipboardData?.files ?? []).filter((f) => f.type.startsWith('image/'))
      if (images.length === 0) return
      event.preventDefault()
      handleFiles(images)
    }
    document.addEventListener('paste', onPaste)
    return () => document.removeEventListener('paste', onPaste)
  }, [handleFiles])

  // Start runs only signed in: a signed-out visitor is given the sign-in instead of the button.
  const handleStartClick = useCallback(() => {
    if (!isReady || startFetcher.state !== 'idle') return
    // Photos a refused Start set aside are left out: that is what "Continue with N photos" sends.
    const okSlots = slots.filter(
      (s): s is Extract<typeof s, {status: 'ok'}> => s.status === 'ok' && !marks[s.s3Key],
    )
    const s3Keys = okSlots.map((s) => s.s3Key)
    submittedPhotoCountRef.current = okSlots.length
    if (isRetrain) retrainDiffRef.current = diffRetrainSet(prefillKeys, s3Keys)
    clearSkippedNotices()
    const formData = new FormData()
    formData.append('intent', 'start-training')
    formData.append('s3Keys', JSON.stringify(s3Keys))
    formData.append('trainingConsent', '1')
    if (returnSessionId) formData.append(RETURN_SESSION_PARAM, returnSessionId)
    // The action lives on `/model` (`routes/_layout.model.tsx`) and is named explicitly: this bench
    // also mounts inside the wall's overlay on `/` (`wall/IdentityIntake.tsx`), a route with no
    // action of its own — a submit to the current URL there answered 405 (dev, 2026-09-22).
    startFetcher.submit(formData, {method: 'POST', action: '/model'})
  }, [isReady, slots, marks, isRetrain, prefillKeys, startFetcher, returnSessionId, clearSkippedNotices])
  const startRef = useRef(handleStartClick)
  startRef.current = handleStartClick

  // The checkout form's submit: `checkout_started` goes out before the page leaves (gtag's beacon
  // transport outlives the unload), once per submit that actually leaves.
  const handleCheckoutSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
    const started = checkoutStartedOnce(checkoutSubmittedRef, currency)
    if (!started) {
      event.preventDefault()
      return
    }
    setCheckoutNotice(null)
    clearSkippedNotices()
    setCheckoutSubmitting(true)
    narcisTrack('checkout_started', started)
  }, [currency, clearSkippedNotices])


  // Back from a paid Checkout with the set restored: the Start the buyer pressed before paying
  // finishes by itself, once — no second press, no photo still being checked.
  const verifying = summary.checking > 0
  useEffect(() => {
    const idle = startFetcher.state === 'idle'
    if (!shouldAutoStart({sessionId: returnSessionId, alreadyStarted: autoStartedRef.current, isAuthenticated, isReady, verifying, idle})) return
    autoStartedRef.current = true
    handleStartClick()
  }, [returnSessionId, isAuthenticated, isReady, verifying, startFetcher.state, handleStartClick])

  // GA4 funnel: fires the instant startFetcher.data carries a fresh response, not once it settles
  // to 'idle' — lastTrackedDataRef (identity, not a one-shot flag) is what guards against a
  // double-fire, so firing earlier costs nothing. This was a live bug (2026-08-01): a first-time
  // Start's success flips userModel.status to 'training' server-side; react-router lands that new
  // loader data and this fetcher's terminal 'idle' state in the SAME commit, which is also the
  // commit where _layout.model.tsx's status branch unmounts <ModelIntake> — so an 'idle'-gated
  // effect here never got to run. The 'loading' commit (fetcher.data already populated from the
  // action response, per react-router's handleFetcherAction) lands earlier, before that unmount,
  // and is otherwise identical for this effect's purposes.
  // training_started (funnel spec §2) — THE conversion point — for a first-time Start; a retrain
  // fires `retrain_started` INSTEAD (spec §10's own line), never both. is_retrain is dropped from
  // training_started's own payload: the dictionary's shape is exact law (photo_count only); the
  // retrain case gets its own distinctly-named event rather than a param on this one.
  // model_training_refused (restored 2026-08-01 — see REFUSAL_REASONS' own comment above) covers
  // every reason the action returns that a user can meet; each also leaves its own UI below.
  useEffect(() => {
    const data = startFetcher.data
    if (!data || lastTrackedDataRef.current === data) return
    lastTrackedDataRef.current = data
    if (data.status === 'queued') {
      // The stash is spent the moment the job is queued — Start deletes the staging objects it
      // points at, so keeping it means the next retrain in this tab restores dead keys and Start
      // 500s on the per-job copy. See `clearStash`'s doc in useModelIntake.
      clearStash()
      paymentTriesRef.current = 0
      // The paid session is now a model: a reload must not run the return again.
      if (settledAddressRef.current) {
        window.history.replaceState(window.history.state, '', settledAddressRef.current)
        settledAddressRef.current = null
      }
      // The money is recorded: GA4's purchase, once per answer (this effect's identity guard) and
      // once per purchase (the action answers `payment` on the Start that consumes it).
      const purchase = purchaseEventOnce(data.payment, safeLocalStorage())
      if (purchase) narcisTrack('purchase', purchase)
      if (isRetrain && retrainDiffRef.current) {
        narcisTrack('retrain_started', retrainDiffRef.current)
      } else {
        narcisTrack('training_started', {photo_count: submittedPhotoCountRef.current})
      }
      // The wall's own overlay (narcis wall plan step 4) — no celebration, no toast, no route
      // change: `onDone` just tells the caller Start actually queued, so it can close the overlay
      // onto the same armed tile and refresh `/mine`. `/model`'s own mount passes none.
      onDone?.()
    } else if (data.status === 'error') {
      if (isTrackedRefusal(data.error)) narcisTrack('model_training_refused', {reason: data.error})
      // The server lost the session the page believed in: the sign-in opens in the rail.
      if (data.error === 'signup-required') setSessionLost(true)
      if (data.error === 'consent-required') {
        setConsentRing(true)
        gate.press(() => startRef.current())
      }
      const named = refusalMarks(data)
      if (Object.keys(named).length > 0) setMarks((prev) => ({...prev, ...named}))
    } else if (data.status === 'payment-required') {
      narcisTrack('model_training_refused', {reason: 'payment-required'})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `gate.press` reads live refs
  }, [startFetcher.data, isRetrain, clearStash, onDone])

  // A paid return whose payment cannot be confirmed yet (the webhook may be late): Start is asked
  // again on its own a few times, then the user is given the reference to quote.
  const paymentPending = startFetcher.data?.status === 'payment-required' && !!returnSessionId
  useEffect(() => {
    if (!paymentPending) {
      setPaymentGaveUp(false)
      return
    }
    if (paymentTriesRef.current >= PAYMENT_RETRIES) {
      setPaymentGaveUp(true)
      return
    }
    const timer = setTimeout(() => {
      paymentTriesRef.current += 1
      startRef.current()
    }, PAYMENT_RETRY_MS)
    return () => clearTimeout(timer)
  }, [paymentPending, startFetcher.data])

  // Grid grows with the set, MIN..MAX, plus one trailing "add" cell while there's still room
  // (canvas states A/B/C).
  const renderedCount = Math.min(MAX_TRAINING_PHOTOS, Math.max(MIN_TRAINING_PHOTOS, slots.length + (isFull ? 0 : 1)))

  // Payment is asked only when nothing has been paid yet — a return from Checkout has, and so has a
  // purchase `/checkout/model` found already open.
  const asksPayment = modelPayment === 'pay' && !returnSessionId && !purchaseOpen
  // Start set photos aside: the same button carries on without them.
  const continuing = isReady && marked > 0
  // A retrain's box stands ticked and locked, unless the server asked for it again.
  const consentGiven = (isRetrain && !consentRing) || gate.checked
  const control = startControl({
    isReady,
    accepted,
    min: MIN_TRAINING_PHOTOS,
    continuing,
    asksPayment,
    isRetrain,
    isAuthenticated,
    consentGiven,
    sessionLost,
    currency,
  })
  const signInShown = control.kind === 'signin'
  // gate_hit: the training gate is the funnel's own name for this moment; the trigger matches the
  // `trigger="model_intake"` SignupGate is rendered with, so the two join once auth_option_selected /
  // magic_link_sent fire. Once per appearance of the sign-in.
  useEffect(() => {
    if (signInShown) narcisTrack('gate_hit', {gate: 'signup', trigger: 'model_intake'})
  }, [signInShown])

  const startBusy = startFetcher.state !== 'idle' || checkoutSubmitting
  const queued = startFetcher.data?.status === 'queued'
  const startEnabled = isReady && !startBusy && !queued
  // Signed in and asked to pay: Start is the checkout form's submit.
  const checkoutStart = asksPayment && isAuthenticated && !continuing
  const startLabel = checkoutSubmitting
    ? 'Opening payment…'
    : returnSessionId
      ? 'Starting your model…'
      : 'Starting…'
  // A retrain, or a Start the gate already let through, runs at once; any other first press asks.
  const pressStart = (fire: () => void) => (isRetrain && !consentRing ? fire() : gate.press(fire))
  const checkoutFormRef = useRef<HTMLFormElement>(null)
  const startButtonEl = startBusy ? (
    <button type="button" className={`${styles.btnYolk} ${styles.btnBusy}`} disabled>
      <span className={styles.spin} aria-hidden="true" />
      {startLabel}
    </button>
  ) : (
    <button
      type="button"
      className={startEnabled ? styles.btnYolk : styles.btnDisabled}
      disabled={!startEnabled}
      onClick={() => pressStart(checkoutStart ? () => checkoutFormRef.current?.requestSubmit() : handleStartClick)}
    >
      {queued ? 'Started' : control.kind === 'button' ? control.label : null}
    </button>
  )

  // ── The moment: one tone for the panel's rule, the state line and the meter ──
  const alreadyTraining = startFetcher.data?.status === 'error' && startFetcher.data.error === 'already-training'
  const startError = startFetcher.data?.status === 'error' ? startFetcher.data.error : null
  const paymentRefused = startFetcher.data?.status === 'payment-required'
  const refused =
    !queued &&
    !alreadyTraining &&
    (marked > 0 || consentRing || paymentGaveUp || (paymentRefused && !returnSessionId) || (!!startError && startError !== 'signup-required'))
  const toneInput = {summary, min: MIN_TRAINING_PHOTOS, starting: startBusy || paymentPending, queued: queued || alreadyTraining, refused}
  const tone = deriveTone(toneInput)
  const line = checkoutSubmitting
    ? 'Opening the payment page'
    : alreadyTraining
      ? 'A model is already being created'
      : stateLine({...toneInput, tone, paidReturn: !!returnSessionId, confirmingPayment: paymentPending && !paymentGaveUp, marked})
  const ledger = ledgerRows(summary, setAdvisories)
  // Ready and nothing running: the top state line offers the way down to Start. It scrolls and never
  // starts anything, so the price and the consent are seen first.
  const canContinue = isReady && !queued && !alreadyTraining && !startBusy && !paymentPending && summary.checking === 0
  const scrollToStart = () => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    startAreaRef.current?.scrollIntoView({behavior: reduced ? 'auto' : 'smooth', block: 'center'})
  }
  const meterFull = accepted >= MIN_TRAINING_PHOTOS
  const body = queued
    ? 'Creating it takes a few hours. We will email you when it is ready.'
    : paymentPending && !paymentGaveUp
      ? 'This takes a moment. We try again on our own.'
      : startBusy && returnSessionId
        ? 'Thank you. Your payment is received and your model starts now.'
        : checkoutSubmitting
          ? 'Your photos stay here while you pay.'
          : startBusy
            ? `We are saving your ${accepted} photos and opening your model.`
            : null
  const foot = footLine({
    inFlight: queued || alreadyTraining,
    starting: !!body && startBusy,
    marked,
    asksPayment,
    isReady,
    hasPhotos: slots.length > 0,
  })
  const markedPhrase = (n: number) => `${n} of your photos ${n === 1 ? 'is' : 'are'}`
  const refusalLine = (() => {
    if (marked === 0) return null
    const parts: string[] = []
    if (summary.unavailable > 0) parts.push(`${markedPhrase(summary.unavailable)} no longer saved.`)
    if (summary.foreign > 0) parts.push(`${markedPhrase(summary.foreign)} from another session.`)
    parts.push(
      isReady
        ? `Continue with the other ${accepted}, or add ${marked === 1 ? 'it' : `the ${marked}`} again.`
        : `Add ${MIN_TRAINING_PHOTOS - accepted} more to reach ${MIN_TRAINING_PHOTOS}.`,
    )
    return parts.join(' ')
  })()

  return (
    <div className={styles.intake}>
      <p className={styles.eyebrow}>Your model · one-time setup</p>
      <h1 className={styles.h1}>One model, created from your photos.</h1>
      <p className={`${styles.sub} ${asksPayment ? styles.subAbovePrice : ''}`}>
        Add {MIN_TRAINING_PHOTOS} to {MAX_TRAINING_PHOTOS} photos of yourself. We check each one as you add it.
      </p>
      {asksPayment && (
        <p className={styles.priceLine}>
          {priceLine(currency)} {GUARANTEE}
        </p>
      )}

      {showModelNote && (
        <div className={styles.modelNote}>
          <span>
            {modelNote === 'ready'
              ? 'Your current photos are here. Add or remove photos, then update your model.'
              : 'We could not finish your last model. Please start again.'}
          </span>
          {modelNote === 'failed' && (
            <button
              type="button"
              className={styles.modelNoteDismiss}
              onClick={() => setNoteDismissed(true)}
              aria-label="Dismiss"
            >
              ✕
            </button>
          )}
        </div>
      )}

      {slots.length === 0 && (
        <div className={styles.guide}>
          <div className={styles.guideCol}>
            <p className={styles.guideLabel}>What works</p>
            <div className={styles.guideTiles}>
              {GUIDANCE_DO.map((g) => (
                <div key={g.caption} className={styles.guideTile}>
                  <img className={styles.guideImg} src={g.img} alt={g.caption} loading="lazy" />
                  <p className={styles.guideCap}><span className={styles.ok}>✓</span> {g.note}</p>
                </div>
              ))}
            </div>
          </div>
          <div className={styles.guideCol}>
            <p className={styles.guideLabel}>What doesn't</p>
            <div className={styles.guideTiles}>
              {GUIDANCE_DONT.map((g) => (
                <div key={g.caption} className={styles.guideTile}>
                  <img className={styles.guideImg} src={g.img} alt={g.caption} loading="lazy" />
                  <p className={styles.guideCap}><span className={styles.no}>✕</span> {g.note}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className={`${styles.panel} ${TONE_CLASS[tone]}`} data-tone={tone}>
        <div className={styles.rule} />
        <div className={styles.bench}>
          <div className={styles.benchMain}>
            {/* On a phone the rail sits after the whole grid, so the moment is said here and the rail's copy is hidden. */}
            <p className={`${styles.stateLine} ${styles.stateTop}`}>
              <span className={styles.dot} />
              {line}
              {canContinue && (
                <>
                  <span aria-hidden="true">·</span>
                  <button type="button" className={styles.continueLink} onClick={scrollToStart}>
                    Continue ↓
                  </button>
                </>
              )}
            </p>
            {(skippedNotices.length > 0 || checkoutNotice) && (
              <div className={styles.notices}>
                {skippedNotices.map((text) => (
                  <div key={text} className={styles.noticeRow}>
                    <Notice kind="warning" ttlMs={0}>
                      {text}
                    </Notice>
                    <button type="button" className={styles.noticeDismiss} onClick={clearSkippedNotices} aria-label="Dismiss">
                      ✕
                    </button>
                  </div>
                ))}
                {checkoutNotice && startFetcher.data === undefined && checkoutNotice.outcome !== 'training' && (
                  <Notice kind={NOTICE_KIND[checkoutNotice.outcome]} ttlMs={0}>
                    {checkoutNotice.text}
                  </Notice>
                )}
                {checkoutNotice && checkoutNotice.outcome === 'training' && (
                  <p className={styles.noticeLine} data-kind="success">
                    {checkoutNotice.text} <Link to="/model">See your model</Link>
                  </p>
                )}
              </div>
            )}

            <div className={styles.slots}>
              {slots.map((slot, i) => {
                const tile = tiles[i]
                return (
                  <div key={slot.id} className={`${styles.slot} ${styles[`tile_${tile.view}`]}`}>
                    {slot.previewUrl ? (
                      <img src={slot.previewUrl} alt="" className={styles.slotImg} />
                    ) : (
                      <div className={styles.ph} aria-hidden="true" />
                    )}
                    {tile.view === 'checking' && <div className={styles.veil} />}
                    {(tile.tag || tile.reason) && (
                      <div className={styles.cap}>
                        {tile.tag && <b>{tile.tag}</b>}
                        {tile.reason}
                      </div>
                    )}
                    <button
                      type="button"
                      className={styles.slotX}
                      onClick={() => removeSlot(slot.id)}
                      aria-label="Remove photo"
                    >
                      ✕
                    </button>
                  </div>
                )
              })}
              {Array.from({length: Math.max(0, renderedCount - slots.length)}, (_, i) => (
                <div
                  key={`empty-${i}`}
                  className={`${styles.slot} ${styles.slotEmpty}`}
                  onClick={() => inputRef.current?.click()}
                  role="button"
                  aria-label="Add photo"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click() }}
                >
                  <span className={styles.slotPlus}>+</span>
                </div>
              ))}
            </div>

            {!isFull && (
              <div
                className={styles.drop}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => inputRef.current?.click()}
                role="button"
                aria-label="Drop photos here or click to select"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click() }}
              >
                <b className={styles.dropMouse}>Click, drag or paste</b>
                <b className={styles.dropTouch}>Tap</b> to add {slots.length === 0 ? 'photos' : 'more photos'} · JPG, PNG or WebP
              </div>
            )}
            <input
              ref={inputRef}
              type="file"
              accept={validImageTypes.join(', ')}
              multiple
              hidden
              onChange={(e) => {
                if (e.target.files) handleFiles(Array.from(e.target.files))
                e.target.value = ''
              }}
            />
          </div>

          <div className={styles.rail}>
            <p className={`${styles.stateLine} ${styles.stateRail}`}>
              <span className={styles.dot} />
              {line}
            </p>
            <div className={styles.meter}>
              <p className={styles.meterCount}>
                {accepted}{' '}
                <small>
                  {isRetrain
                    ? `photo${accepted === 1 ? '' : 's'} · add more or swap`
                    : isReady
                      ? 'photos accepted'
                      : `of ${MIN_TRAINING_PHOTOS} accepted`}
                </small>
              </p>
              <div className={`${styles.meterBar} ${meterFull ? styles.meterBarFull : ''}`}>
                <i style={{width: `${Math.min(100, Math.round((accepted / MIN_TRAINING_PHOTOS) * 100))}%`}} />
              </div>
              {body ? (
                <p className={styles.meterBody}>{body}</p>
              ) : accepted > 0 || ledger.length > 1 ? (
                <ul className={styles.ledger}>
                  {ledger.map((row) => (
                    <li key={row.text} className={`${styles.ledgerRow} ${LEDGER_TONE_CLASS[row.tone]}`}>
                      <span className={styles.dot} />
                      <span className={styles.ledgerText}>
                        {row.text}
                        {row.sub && <span className={styles.ledgerSub}>{row.sub}</span>}
                      </span>
                      {row.count !== undefined && <span className={styles.ledgerCount}>{row.count}</span>}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className={styles.meterNote}>
                  We check each photo when you add it. You need at least {MIN_TRAINING_PHOTOS}. Up to{' '}
                  {MAX_TRAINING_PHOTOS} gives the best results.
                </p>
              )}
            </div>

            {refusalLine && !body && (
              <p className={`${styles.railLine} ${styles.toneNeeds}`}>
                <span className={styles.dot} />
                <span>{refusalLine}</span>
              </p>
            )}
            {startError === 'invalid-photo-count' && marked === 0 && (
              <p className={`${styles.railLine} ${styles.toneNeeds}`}>
                <span className={styles.dot} />
                <span>Start needs at least {MIN_TRAINING_PHOTOS} photos of yours. Add more and try again.</span>
              </p>
            )}
            {alreadyTraining && (
              <p className={`${styles.railLine} ${styles.toneReady}`}>
                <span className={styles.dot} />
                <span>
                  Your model is already being created. We will email you when it is ready.{' '}
                  <Link to="/model">See your model</Link>
                </span>
              </p>
            )}
            {paymentRefused && !returnSessionId && (
              <p className={`${styles.railLine} ${styles.toneNeeds}`}>
                <span className={styles.dot} />
                <span>We could not confirm your payment yet. Try again in a minute.</span>
              </p>
            )}
            {paymentGaveUp && returnSessionId && (
              <p className={`${styles.railLine} ${styles.toneNeeds}`}>
                <span className={styles.dot} />
                <span>
                  We could not confirm your payment. Write to{' '}
                  <a href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(`Payment ${returnSessionId}`)}`}>{CONTACT_EMAIL}</a>{' '}
                  and quote <code className={styles.reference}>{returnSessionId}</code>.
                </span>
              </p>
            )}
            {startError &&
              !['already-training', 'invalid-photo-count', 'photos-unavailable', 'consent-required', 'signup-required'].includes(startError) && (
                <p className={`${styles.railLine} ${styles.toneNeeds}`}>
                  <span className={styles.dot} />
                  <span>Something went wrong. Please try again.</span>
                </p>
              )}
            {startError === 'photos-unavailable' && marked === 0 && (
              <p className={`${styles.railLine} ${styles.toneNeeds}`}>
                <span className={styles.dot} />
                <span>Some of these photos are no longer saved. Remove them and add new ones.</span>
              </p>
            )}

            {/* Intake consent (funnel spec §8, amended §10) — unticked by default, shown once
                there's an actual Start decision to make. Disables Start until checked; the
                server enforces the same rule independently (see the action's own comment). For
                a retrain, PRE-TICKED and DISABLED — already granted at first training, no
                re-tick required — the box still renders (with the privacy link) so the fact
                stays visible, it just can't be unchecked. A server that still asks for consent
                (consent-required) rings the box and lets it be ticked. */}
            <div className={styles.startArea} ref={startAreaRef}>
            {isReady && !queued && isRetrain && !consentRing && (
              <label className={styles.consent}>
                <input type="checkbox" checked disabled readOnly />
                <span>
                  {CONSENT_SENTENCE} <Link to="/terms#tos-10">Privacy policy</Link>
                </span>
              </label>
            )}
            {isReady && !queued && gate.open && (
              <div className={`${styles.consentReveal} ${consentRing ? styles.consentRing : ''}`}>
                <label className={styles.consent}>
                  <input
                    ref={consentBoxRef}
                    type="checkbox"
                    checked={gate.checked}
                    onChange={(e) => {
                      gate.tick(e.target.checked)
                      if (e.target.checked) setConsentRing(false)
                    }}
                  />
                  <span>
                    {CONSENT_SENTENCE} <Link to="/terms#tos-10">Privacy policy</Link>
                  </span>
                </label>
                {consentRing && (
                  <p className={`${styles.railLine} ${styles.toneNeeds}`}>
                    <span className={styles.dot} />
                    <span>Tick the box to continue.</span>
                  </p>
                )}
              </div>
            )}
            {/* `startFetcher.state`: the action takes a few seconds (the photos' keys, the job
                row, the launch) — the button says so and holds, rather than reading as a press
                that did nothing (operator, wall dev 2026-09-22). The checkout holds it the same
                way until Stripe's page replaces this one. */}
            {queued ? (
              <Link to="/account/packs" className={styles.btnGhost}>
                See your packs while you wait
              </Link>
            ) : signInShown ? (
              // The sign-in returns to this intake with the box ticked (`consentReturnAddress`).
              <div className={styles.signIn}>
                <SignupGate
                  bare
                  funnel={funnel}
                  authAction={authAction}
                  redirectTo={consentReturnAddress(signupRedirectTo)}
                  trigger="model_intake"
                  termsHref="/terms"
                  privacyHref="/terms#tos-10"
                />
                <p className={styles.railFoot}>New here? Your account is created when you sign in.</p>
              </div>
            ) : checkoutStart ? (
              // The photos wait in the intake's sessionStorage stash; Checkout returns to
              // `signupRedirectTo`, which reopens this intake.
              <Form ref={checkoutFormRef} method="post" action="/checkout/model" reloadDocument onSubmit={handleCheckoutSubmit}>
                <input type="hidden" name="returnTo" value={signupRedirectTo} />
                {startButtonEl}
              </Form>
            ) : (
              startButtonEl
            )}
            {asksPayment && isReady && !startBusy && (
              <p className={styles.railNote}>
                {priceLine(currency)} {GUARANTEE}
              </p>
            )}
            </div>
            {returnSessionId && !isReady && !startBusy && (
              <p className={styles.railNote}>Your payment is received. Add your photos to create your model.</p>
            )}
            {foot && <p className={styles.railFoot}>{foot}</p>}
          </div>
        </div>
      </div>
    </div>
  )
}
